import React, { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { leaseAPI, unitAPI, tenantAPI } from '../../lib/api';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Plus,
  FileText,
  Calendar,
  DollarSign,
  User,
  Home,
  CheckCircle,
  Upload,
  Download,
  Trash2,
  File,
  Loader2,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react';
import { useI18n } from '../../context/I18nContext';

const Leases = () => {
  const { t } = useI18n();
  const [leases, setLeases] = useState([]);
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedLease, setSelectedLease] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [renewingLease, setRenewingLease] = useState(null);
  const [expiringLeases, setExpiringLeases] = useState([]);
  const [renewForm, setRenewForm] = useState({
    start_date: '',
    end_date: '',
    monthly_rent: '',
  });
  const [formData, setFormData] = useState({
    unit_id: '',
    tenant_id: '',
    start_date: '',
    end_date: '',
    monthly_rent: '',
    deposit_amount: '',
    terms: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [leasesRes, unitsRes, tenantsRes, expiringRes] = await Promise.all([
        leaseAPI.getAll(),
        unitAPI.getAll(),
        tenantAPI.getAll(),
        leaseAPI.getExpiring(60).catch(() => ({ data: [] })),
      ]);
      setLeases(leasesRes.data);
      setUnits(unitsRes.data);
      setTenants(tenantsRes.data);
      setExpiringLeases(expiringRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        monthly_rent: parseFloat(formData.monthly_rent),
        deposit_amount: parseFloat(formData.deposit_amount)
      };
      await leaseAPI.create(submitData);
      toast.success('Lease created successfully');
      setDialogOpen(false);
      setFormData({
        unit_id: '',
        tenant_id: '',
        start_date: '',
        end_date: '',
        monthly_rent: '',
        deposit_amount: '',
        terms: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lease');
    }
  };

  const handleActivate = async (leaseId) => {
    try {
      await leaseAPI.activate(leaseId);
      toast.success('Lease activated');
      loadData();
    } catch (error) {
      toast.error('Failed to activate lease');
    }
  };

  const openRenewDialog = (lease) => {
    setRenewingLease(lease);
    const today = new Date();
    const oneYear = new Date(today);
    oneYear.setFullYear(oneYear.getFullYear() + 1);
    setRenewForm({
      start_date: lease.end_date || today.toISOString().split('T')[0],
      end_date: oneYear.toISOString().split('T')[0],
      monthly_rent: lease.monthly_rent || '',
    });
    setRenewDialogOpen(true);
  };

  const handleRenew = async (e) => {
    e.preventDefault();
    if (!renewingLease) return;
    try {
      await leaseAPI.renew(renewingLease.id, {
        start_date: renewForm.start_date,
        end_date: renewForm.end_date,
        monthly_rent: parseFloat(renewForm.monthly_rent),
      });
      toast.success('Lease renewed successfully');
      setRenewDialogOpen(false);
      setRenewingLease(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to renew lease');
    }
  };


  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedLease) return;

    // Check file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only PDF and Word documents are allowed');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);

    try {
      // Read file as base64
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Data = reader.result.split(',')[1]; // Remove data:...;base64, prefix
        
        try {
          await api.post('/documents/upload', {
            lease_id: selectedLease.id,
            filename: file.name,
            file_data: base64Data
          });
          
          toast.success('Document uploaded successfully');
          setUploadDialogOpen(false);
          loadData();
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Failed to upload document');
        } finally {
          setUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to read file');
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(doc.url, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDeleteDocument = async (docId, leaseId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      await api.delete(`/documents/${docId}?lease_id=${leaseId}`);
      toast.success('Document deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const openUploadDialog = (lease) => {
    setSelectedLease(lease);
    setUploadDialogOpen(true);
  };

  const getUnitInfo = (unitId) => {
    const unit = units.find(u => u.id === unitId);
    return unit ? `Unit ${unit.unit_number}` : 'Unknown Unit';
  };

  const getTenantInfo = (tenantId) => {
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant?.name || 'Unknown Tenant';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case 'PENDING': return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      case 'EXPIRED': return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
      case 'TERMINATED': return 'bg-red-500/10 text-red-400 border-red-500/20';
      default: return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };

  const vacantUnits = units.filter(u => u.status === 'VACANT');

  if (loading) {
    return (
      <DashboardLayout type="landlord" title={t('leases.title')}>
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-24 bg-card animate-pulse" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout type="landlord" title={t('leases.title')}>
      <div className="space-y-6" data-testid="leases-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-muted-foreground">Manage lease agreements and documents</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-gold text-white hover:bg-gold-light"
                disabled={vacantUnits.length === 0 || tenants.length === 0}
                data-testid="create-lease-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Lease
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[500px] bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-serif">Create New Lease</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm uppercase tracking-wider">Unit</Label>
                    <Select 
                      value={formData.unit_id} 
                      onValueChange={(v) => {
                        const unit = units.find(u => u.id === v);
                        setFormData({
                          ...formData, 
                          unit_id: v,
                          monthly_rent: unit?.rent_amount || '',
                          deposit_amount: unit?.deposit_amount || unit?.rent_amount || ''
                        });
                      }}
                    >
                      <SelectTrigger className="bg-background border-[#e5e7eb]" data-testid="lease-unit-select">
                        <SelectValue placeholder="Select unit" />
                      </SelectTrigger>
                      <SelectContent>
                        {vacantUnits.map(u => (
                          <SelectItem key={u.id} value={u.id}>Unit {u.unit_number}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm uppercase tracking-wider">Tenant</Label>
                    <Select 
                      value={formData.tenant_id} 
                      onValueChange={(v) => setFormData({...formData, tenant_id: v})}
                    >
                      <SelectTrigger className="bg-background border-[#e5e7eb]" data-testid="lease-tenant-select">
                        <SelectValue placeholder="Select tenant" />
                      </SelectTrigger>
                      <SelectContent>
                        {tenants.map(t => (
                          <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm uppercase tracking-wider">Start Date</Label>
                    <Input
                      name="start_date"
                      type="date"
                      value={formData.start_date}
                      onChange={handleChange}
                      className="bg-background border-[#e5e7eb]"
                      required
                      data-testid="lease-start-date"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm uppercase tracking-wider">End Date</Label>
                    <Input
                      name="end_date"
                      type="date"
                      value={formData.end_date}
                      onChange={handleChange}
                      className="bg-background border-[#e5e7eb]"
                      required
                      data-testid="lease-end-date"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm uppercase tracking-wider">Monthly Rent ($)</Label>
                    <Input
                      name="monthly_rent"
                      type="number"
                      step="0.01"
                      value={formData.monthly_rent}
                      onChange={handleChange}
                      className="bg-background border-[#e5e7eb]"
                      required
                      data-testid="lease-rent-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-muted-foreground text-sm uppercase tracking-wider">Deposit ($)</Label>
                    <Input
                      name="deposit_amount"
                      type="number"
                      step="0.01"
                      value={formData.deposit_amount}
                      onChange={handleChange}
                      className="bg-background border-[#e5e7eb]"
                      required
                      data-testid="lease-deposit-input"
                    />
                  </div>
                </div>

                <Button type="submit" className="w-full bg-gold text-white hover:bg-gold-light" data-testid="save-lease-btn">
                  Create Lease
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent className="sm:max-w-[400px] bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-serif">Upload Lease Document</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground mb-4">
                Upload a PDF or Word document for this lease agreement.
              </p>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".pdf,.doc,.docx"
                className="hidden"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                className="w-full bg-gold text-white hover:bg-gold-light"
                disabled={uploading}
                data-testid="upload-document-btn"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Choose File
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Maximum file size: 10MB
              </p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Expiring Leases Alert */}
        {expiringLeases.length > 0 && (
          <Card className="bg-amber-500/5 border-amber-500/20" data-testid="expiring-leases-alert">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-amber-400 text-sm">
                    {expiringLeases.length} lease{expiringLeases.length > 1 ? 's' : ''} expiring in the next 60 days
                  </h4>
                  <div className="mt-2 space-y-1">
                    {expiringLeases.map(l => (
                      <div key={l.id} className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          {getUnitInfo(l.unit_id)} — {getTenantInfo(l.tenant_id)} — Ends {l.end_date}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 text-xs border-gold/30 text-gold hover:bg-gold/10 ml-4"
                          onClick={() => openRenewDialog(l)}
                          data-testid={`quick-renew-${l.id}`}
                        >
                          <RefreshCw className="w-3 h-3 mr-1" /> Renew
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Renew Dialog */}
        <Dialog open={renewDialogOpen} onOpenChange={setRenewDialogOpen}>
          <DialogContent className="sm:max-w-[420px] bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-serif">Renew Lease</DialogTitle>
            </DialogHeader>
            {renewingLease && (
              <div className="text-sm text-muted-foreground mb-4">
                Renewing lease for {getUnitInfo(renewingLease.unit_id)} — {getTenantInfo(renewingLease.tenant_id)}
              </div>
            )}
            <form onSubmit={handleRenew} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm uppercase tracking-wider">New Start Date</Label>
                  <Input type="date" value={renewForm.start_date} onChange={(e) => setRenewForm({...renewForm, start_date: e.target.value})} className="bg-background border-[#e5e7eb]" required data-testid="renew-start-date" />
                </div>
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-sm uppercase tracking-wider">New End Date</Label>
                  <Input type="date" value={renewForm.end_date} onChange={(e) => setRenewForm({...renewForm, end_date: e.target.value})} className="bg-background border-[#e5e7eb]" required data-testid="renew-end-date" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-muted-foreground text-sm uppercase tracking-wider">Monthly Rent ($)</Label>
                <Input type="number" step="0.01" value={renewForm.monthly_rent} onChange={(e) => setRenewForm({...renewForm, monthly_rent: e.target.value})} className="bg-background border-[#e5e7eb]" required data-testid="renew-rent" />
              </div>
              <p className="text-xs text-muted-foreground">The current lease will be marked as expired and a new active lease will be created. The tenant keeps access without needing a new invite.</p>
              <Button type="submit" className="w-full bg-gold text-white hover:bg-gold-light" data-testid="confirm-renew-btn">
                <RefreshCw className="w-4 h-4 mr-2" /> Confirm Renewal
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Leases List */}
        {leases.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-16 text-center">
              <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-serif mb-2">No Leases Yet</h3>
              <p className="text-muted-foreground mb-6">
                Create your first lease agreement
              </p>
              <Button 
                onClick={() => setDialogOpen(true)} 
                className="bg-gold text-white hover:bg-gold-light"
                disabled={vacantUnits.length === 0 || tenants.length === 0}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Lease
              </Button>
              {(vacantUnits.length === 0 || tenants.length === 0) && (
                <p className="text-sm text-amber-400 mt-4">
                  {vacantUnits.length === 0 ? 'You need vacant units to create a lease' : 'You need tenants to create a lease'}
                </p>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {leases.map((lease, index) => (
              <Card 
                key={lease.id} 
                className="bg-card border-border hover:border-gold/30 transition-all duration-300 card-animate"
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`lease-card-${lease.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className="w-12 h-12 bg-gold/10 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-6 h-6 text-gold" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h3 className="font-medium">{getUnitInfo(lease.unit_id)}</h3>
                          <span className={`px-2 py-0.5 text-xs uppercase tracking-wider border ${getStatusColor(lease.status)}`}>
                            {lease.status}
                          </span>
                          {lease.renewed_from && (
                            <span className="px-2 py-0.5 text-xs uppercase tracking-wider border bg-blue-500/10 text-blue-400 border-blue-500/20" data-testid={`renewed-badge-${lease.id}`}>
                              Renewed
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {getTenantInfo(lease.tenant_id)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {lease.start_date} - {lease.end_date}
                          </span>
                        </div>
                        
                        {/* Documents Section */}
                        <div className="mt-4 pt-4 border-t border-border">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs uppercase tracking-wider text-muted-foreground">Documents</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openUploadDialog(lease)}
                              className="text-gold hover:text-gold-light"
                              data-testid={`upload-btn-${lease.id}`}
                            >
                              <Upload className="w-4 h-4 mr-1" />
                              Upload
                            </Button>
                          </div>
                          {lease.documents && lease.documents.length > 0 ? (
                            <div className="space-y-2">
                              {lease.documents.map((doc) => (
                                <div key={doc.id} className="flex items-center justify-between bg-background/50 p-2">
                                  <div className="flex items-center gap-2">
                                    <File className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-sm truncate max-w-[200px]">{doc.filename}</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDownload(doc)}
                                      className="text-blue-400 hover:text-blue-300"
                                      data-testid={`download-doc-${doc.id}`}
                                    >
                                      <Download className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteDocument(doc.id, lease.id)}
                                      className="text-red-400 hover:text-red-300"
                                      data-testid={`delete-doc-${doc.id}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">No documents uploaded</p>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      <div className="text-right">
                        <p className="text-sm text-muted-foreground">Monthly Rent</p>
                        <p className="text-xl font-semibold text-gold">${lease.monthly_rent?.toLocaleString()}</p>
                      </div>
                      {lease.status === 'PENDING' && (
                        <Button 
                          onClick={() => handleActivate(lease.id)}
                          className="bg-emerald-600 hover:bg-emerald-500 text-white"
                          data-testid={`activate-lease-${lease.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Activate
                        </Button>
                      )}
                      {(lease.status === 'ACTIVE' || lease.status === 'EXPIRED') && (
                        <Button
                          variant="outline"
                          className="border-gold/30 text-gold hover:bg-gold/10"
                          onClick={() => openRenewDialog(lease)}
                          data-testid={`renew-lease-${lease.id}`}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Renew
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Leases;
